// The perspective ground — the record that calibrates a photographic plate's
// ground plane, and the pure maths that projects on and off it. One home for
// both, shared by every tool that stands a figure at a depth in a photograph:
// Arcade is the first consumer (a shot's playable stage), Marionette is the
// second (a puppet at depth in a plate, under a dollying camera).
//
// COORDINATE SPACE, declared once and binding on every function here:
//
//   GROUND space (inputs)  — x lateral, 0 on the camera axis, +x to the RIGHT;
//                            z depth, distance from the camera plane, z > 0
//                            increasing AWAY from camera; h height above the
//                            ground plane, +h UP. All three share one unit,
//                            the GROUND UNIT, whose physical meaning the
//                            PerspectiveGround record pins via referenceHeight
//                            (the height of a person in ground units).
//
//   CANVAS space (outputs) — CSS-style canvas px against the PLATE: origin at
//                            the plate's TOP-LEFT, +x right, +y DOWN. This is
//                            Marionette's canvas-px container convention, NOT
//                            Phaser world units; a consumer with a different
//                            screen space owns its own (usually identity)
//                            mapping.
//
// The camera model is a level pinhole: the camera sits cameraHeight ground
// units above the plane, looks parallel to it, and a shifted principal point
// (horizonY, vanishingX) stands in for tilt — which for a ground projection is
// exact. A ground point (x, z) lands at
//
//   canvasX = vanishingX + zoom · x / z
//   canvasY = horizonY   + zoom · height / z
//
// so the horizon is the image of z = ∞, everything below it is ground, and a
// figure's on-screen height falls off as 1/z. `zoom` is the focal length in
// canvas px. NOTHING here imports a renderer, a canvas, or a browser global —
// this module must stay runnable in bare Node, because the standing proof that
// any tool can consume it is a headless test that does exactly that.
//
// CAMERA IS A PARAMETER, NEVER AN ASSUMPTION. Every projection takes the
// GroundCamera it projects through. Arcade holds one camera fixed per shot;
// Marionette dollies and cranes it per frame. A record → camera derivation
// exists (cameraFromGround) but nothing below it caches.

// ── The camera ───────────────────────────────────────────────────────────────

/** The pinhole a projection looks through. Plain data on purpose: animate any
 *  field per frame (a crane is `height`, a dolly-zoom is `zoom` against a
 *  compensating `height`/`horizonY`, a pan is `vanishingX`). */
export interface GroundCamera {
  /** Camera height above the ground plane, ground units. > 0. */
  height: number;
  /** Canvas y of the horizon — the image of infinite depth. May be negative
   *  (a downward-framed shot puts the horizon above the plate's top edge). */
  horizonY: number;
  /** Focal length, canvas px: how many px one ground unit subtends at z = 1.
   *  The projective gauge — see cameraFromGround for the convention that pins
   *  it when deriving from a record. > 0. */
  zoom: number;
  /** Canvas x of the depth axis' vanishing point (the principal point). */
  vanishingX: number;
}

// ── The record ───────────────────────────────────────────────────────────────

export const PERSPECTIVE_GROUND_SCHEMA = 'gooddesign.anim.perspective-ground/v0';

/** A person-height gauge as authored ON the plate: feet at canvas (x, y),
 *  figure heightPx tall. The editor drags these against the photograph's own
 *  people; they are the record's calibration evidence, and they must satisfy
 *  the gauge law (see validatePerspectiveGround). */
export interface PerspectiveGauge {
  x: number;
  y: number;
  heightPx: number;
}

/** The scene's sun, for matching a figure's cast shadow to the plate's.
 *  Azimuth is a CANVAS direction of the thrown shadow (0 = +x/right, 90 = +y/
 *  down toward camera, 180 = left, 270 = up/away); elevation is the sun's
 *  height above the horizon (low sun → long shadow); strength is the shadow's
 *  opacity; softness is its edge blur in canvas px at the silhouette's tip. */
export interface GroundSun {
  azimuthDeg: number;
  /** 1..89. */
  elevationDeg: number;
  /** 0..1. */
  strength: number;
  /** >= 0, canvas px. */
  softness: number;
}

/** One shot's calibrated perspective ground: the plate it was authored
 *  against, the camera geometry read off it by eye, the near/far person
 *  gauges that pinned the read, and the plate's sun.
 *
 *  THE GAUGE LAW. With a level pinhole, a person of height R (ground units)
 *  whose feet sit at canvas y projects heightPx = R · (y − horizonY) / H,
 *  independent of zoom — so the two gauges, the horizon and the camera height
 *  are one over-determined system, which is exactly what makes calibration by
 *  eye work: the editor solves the law along whichever handle is dragged and
 *  re-derives the rest. A record whose gauges break the law is not a looser
 *  calibration, it is a self-contradiction, and validation refuses it. */
export interface PerspectiveGround {
  schema: typeof PERSPECTIVE_GROUND_SCHEMA;
  /** The plate this ground was calibrated against, canvas px. */
  plate: { widthPx: number; heightPx: number };
  /** A person's height in ground units — the record's unit definition. */
  referenceHeight: number;
  /** Camera height above the ground plane, ground units. */
  cameraHeight: number;
  /** Canvas y of the horizon. */
  horizonY: number;
  /** Canvas x of the depth vanishing point. Absent → the plate's centre. */
  vanishingX?: number;
  /** The near calibration gauge — the lower (closer) of the two. */
  nearGauge: PerspectiveGauge;
  /** The far calibration gauge — must sit ABOVE the near one on the plate. */
  farGauge: PerspectiveGauge;
  /** The plate's sun. */
  sun: GroundSun;
}

/** The walkable ground as a polygon in GROUND (x, z): an outer ring, with
 *  optional hole rings carved out of it (a building's footprint, a tree bed).
 *  Rings may wind either way; at least 3 points each. A point ON any boundary
 *  counts as walkable — an actor may stand exactly on the kerb it walked up
 *  against, including a hole's kerb. */
export interface WalkablePolygon {
  outer: ReadonlyArray<GroundPoint>;
  holes?: ReadonlyArray<ReadonlyArray<GroundPoint>>;
}

export interface GroundPoint {
  x: number;
  z: number;
}

// ── Validation ───────────────────────────────────────────────────────────────
// House shape: return human-readable problems, empty === valid, and a loader
// turns non-empty into a HARD failure. Pure — no registry, no I/O.

/** How far a gauge's stored heightPx may sit from the gauge law before the
 *  record counts as self-contradictory: whichever is larger of 3 canvas px
 *  (editor rounding) or 4% of the lawful height. */
const GAUGE_LAW_TOLERANCE_PX = 3;
const GAUGE_LAW_TOLERANCE_FRAC = 0.04;

export function validatePerspectiveGround(pg: PerspectiveGround): string[] {
  const p: string[] = [];
  const num = (v: unknown, path: string): v is number => {
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      p.push(`${path} must be a finite number (got ${JSON.stringify(v)})`);
      return false;
    }
    return true;
  };
  const pos = (v: unknown, path: string) => {
    if (num(v, path) && v <= 0) p.push(`${path} must be > 0 (got ${v})`);
  };

  if (pg.schema !== PERSPECTIVE_GROUND_SCHEMA) {
    p.push(`schema is '${String(pg.schema)}', expected '${PERSPECTIVE_GROUND_SCHEMA}'`);
  }
  if (pg.plate) {
    pos(pg.plate.widthPx, 'plate.widthPx');
    pos(pg.plate.heightPx, 'plate.heightPx');
  } else p.push('plate is required');
  pos(pg.referenceHeight, 'referenceHeight');
  pos(pg.cameraHeight, 'cameraHeight');
  num(pg.horizonY, 'horizonY');
  if (pg.vanishingX !== undefined) num(pg.vanishingX, 'vanishingX');

  const gauge = (g: PerspectiveGauge | undefined, at: 'nearGauge' | 'farGauge') => {
    if (!g) {
      p.push(`${at} is required`);
      return;
    }
    num(g.x, `${at}.x`);
    num(g.y, `${at}.y`);
    pos(g.heightPx, `${at}.heightPx`);
  };
  gauge(pg.nearGauge, 'nearGauge');
  gauge(pg.farGauge, 'farGauge');

  if (pg.sun) {
    num(pg.sun.azimuthDeg, 'sun.azimuthDeg');
    if (num(pg.sun.elevationDeg, 'sun.elevationDeg') && (pg.sun.elevationDeg < 1 || pg.sun.elevationDeg > 89)) {
      p.push(`sun.elevationDeg must be in 1..89 (got ${pg.sun.elevationDeg})`);
    }
    if (num(pg.sun.strength, 'sun.strength') && (pg.sun.strength < 0 || pg.sun.strength > 1)) {
      p.push(`sun.strength must be in 0..1 (got ${pg.sun.strength})`);
    }
    if (num(pg.sun.softness, 'sun.softness') && pg.sun.softness < 0) {
      p.push(`sun.softness must be >= 0 (got ${pg.sun.softness})`);
    }
  } else p.push('sun is required');

  // Geometry — only meaningful once the numbers themselves are sound.
  if (p.length > 0) return p;

  if (pg.nearGauge.y <= pg.horizonY) p.push(`nearGauge.y (${pg.nearGauge.y}) must be below the horizon (${pg.horizonY})`);
  if (pg.farGauge.y <= pg.horizonY) p.push(`farGauge.y (${pg.farGauge.y}) must be below the horizon (${pg.horizonY})`);
  if (pg.farGauge.y >= pg.nearGauge.y) {
    p.push(`farGauge.y (${pg.farGauge.y}) must sit above nearGauge.y (${pg.nearGauge.y}) on the plate`);
  }
  if (p.length > 0) return p;

  for (const [g, at] of [
    [pg.nearGauge, 'nearGauge'],
    [pg.farGauge, 'farGauge'],
  ] as const) {
    const lawful = (pg.referenceHeight * (g.y - pg.horizonY)) / pg.cameraHeight;
    const tolerance = Math.max(GAUGE_LAW_TOLERANCE_PX, lawful * GAUGE_LAW_TOLERANCE_FRAC);
    if (Math.abs(g.heightPx - lawful) > tolerance) {
      p.push(
        `${at}.heightPx (${g.heightPx}) breaks the gauge law — at y=${g.y} a ` +
          `${pg.referenceHeight}-unit figure under this horizon/cameraHeight stands ${lawful.toFixed(1)}px`,
      );
    }
  }

  return p;
}

export function validateWalkablePolygon(poly: WalkablePolygon): string[] {
  const p: string[] = [];
  const ring = (pts: ReadonlyArray<GroundPoint> | undefined, at: string) => {
    if (!Array.isArray(pts)) {
      p.push(`${at} must be an array of points`);
      return;
    }
    if (pts.length < 3) p.push(`${at} needs at least 3 points (got ${pts.length})`);
    pts.forEach((pt, i) => {
      if (typeof pt?.x !== 'number' || !Number.isFinite(pt.x)) p.push(`${at}[${i}].x must be a finite number`);
      if (typeof pt?.z !== 'number' || !Number.isFinite(pt.z)) p.push(`${at}[${i}].z must be a finite number`);
    });
  };
  ring(poly.outer, 'outer');
  if (poly.holes !== undefined) {
    if (!Array.isArray(poly.holes)) p.push('holes must be an array of rings');
    else poly.holes.forEach((h, i) => ring(h, `holes[${i}]`));
  }
  return p;
}

// ── Record → camera ──────────────────────────────────────────────────────────

/** Derive the camera a record's ground projects through.
 *
 *  ZOOM CONVENTION. A single image cannot distinguish focal length from
 *  distance (move the camera back and zoom in: same picture, different z
 *  numbers), so `zoom` is projectively free. It is pinned to plate.heightPx —
 *  "a normal lens, focal length = frame height" — so that two records over the
 *  same plate always mean the same ground units, and a hand-written record is
 *  reproducible without this comment. Consumers animating a dolly-zoom vary
 *  the returned camera; the RECORD's zoom is always this convention. */
export function cameraFromGround(pg: PerspectiveGround): GroundCamera {
  return {
    height: pg.cameraHeight,
    horizonY: pg.horizonY,
    zoom: pg.plate.heightPx,
    vanishingX: pg.vanishingX ?? pg.plate.widthPx / 2,
  };
}

// ── Projection ───────────────────────────────────────────────────────────────

/** Where ground point (x, z), h units above the plane, lands on the canvas.
 *  z must be > 0 (at/behind the camera plane there is no image to land on —
 *  that is corrupted simulation state, and it throws rather than returning a
 *  plausible wrong point). */
export function project(x: number, z: number, camera: GroundCamera, h = 0): { x: number; y: number } {
  if (!(z > 0)) throw new Error(`ground-math: project() needs z > 0 (got ${z}) — the point is at or behind the camera`);
  return {
    x: camera.vanishingX + (camera.zoom * x) / z,
    y: camera.horizonY + (camera.zoom * (camera.height - h)) / z,
  };
}

/** The ground point whose image is canvas (screenX, screenY), assuming it lies
 *  ON the plane (h = 0). Null when screenY is at or above the horizon — a
 *  click on the sky names no ground point, and for an authoring surface that
 *  is an expected event, not corruption. */
export function unproject(screenX: number, screenY: number, camera: GroundCamera): GroundPoint | null {
  const drop = screenY - camera.horizonY;
  if (drop <= 0) return null;
  const z = (camera.zoom * camera.height) / drop;
  return { x: ((screenX - camera.vanishingX) * z) / camera.zoom, z };
}

/** Canvas px one ground unit subtends at depth z: the size law. A figure of
 *  height R ground units at depth z is R · scaleAt(z, camera) px tall. */
export function scaleAt(z: number, camera: GroundCamera): number {
  if (!(z > 0)) throw new Error(`ground-math: scaleAt() needs z > 0 (got ${z})`);
  return camera.zoom / z;
}

/** Canvas y of the horizon — where the ground vanishes. */
export function horizonY(camera: GroundCamera): number {
  return camera.horizonY;
}

/** The screen shear a cast-shadow silhouette rakes along: canvas displacement
 *  per unit of FIGURE HEIGHT, so a silhouette point h above its contact point
 *  draws at contact + h · shear. Feet (h = 0) stay pinned; a low sun throws
 *  the head far. cot(elevation) is the length law; azimuth aims it. */
export function sunShear(sun: GroundSun): { x: number; y: number } {
  const az = (sun.azimuthDeg * Math.PI) / 180;
  const len = 1 / Math.tan((sun.elevationDeg * Math.PI) / 180);
  return { x: Math.cos(az) * len, y: Math.sin(az) * len };
}

// ── Walkable polygon ─────────────────────────────────────────────────────────

/** Even-odd ray cast with an explicit boundary verdict, so "standing exactly
 *  on the kerb" can count as inside for the outer ring AND for a hole. */
function ringSide(x: number, z: number, ring: ReadonlyArray<GroundPoint>): 'inside' | 'boundary' | 'outside' {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const cross = (b.x - a.x) * (z - a.z) - (b.z - a.z) * (x - a.x);
    const dot = (x - a.x) * (x - b.x) + (z - a.z) * (z - b.z);
    if (Math.abs(cross) < 1e-6 && dot <= 0) return 'boundary';
    if (a.z > z !== b.z > z && x < ((b.x - a.x) * (z - a.z)) / (b.z - a.z) + a.x) {
      inside = !inside;
    }
  }
  return inside ? 'inside' : 'outside';
}

/** Is ground point (x, z) on the walkable ground — inside (or on) the outer
 *  ring, and not STRICTLY inside any hole? A hole's kerb itself is walkable. */
export function pointInPolygon(x: number, z: number, polygon: WalkablePolygon): boolean {
  if (ringSide(x, z, polygon.outer) === 'outside') return false;
  for (const hole of polygon.holes ?? []) {
    if (ringSide(x, z, hole) === 'inside') return false;
  }
  return true;
}
